import './App.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocalStorage } from './components/useLocalStorage';
import Step from './components/Step';
import Puzzle from './components/Puzzle';
import type { StepConfig } from './types';

// StepConfig is imported from './types'

function App() {
    const [progress, setProgress] = useLocalStorage<{ solved: boolean[] }>('katepw:progress:v1', {
        solved: [false, false, false, false, false, false, false],
    });

    const steps: StepConfig[] = useMemo(
        () => [
            { type: 'wordle', answer: 'heart', message: 'Yours is the most beautiful. 💖' },
            { type: 'wordle', answer: 'fresh', message: 'It\'s not time for a fresh start. But it\'s time for something new. 🌱' },
            { type: 'wordle', answer: 'today', message: 'Something wonderful starts today. Pack a bag! 🎒' },
            { type: 'timegate', message: 'Well done! Now go pack a weekend bag - something comfortable and cozy for the countryside. Then get in the car! 🚗', unlockAfterHours: 1.5, completionMessage: 'Sorry about the wait. Without this you\'d solve them all too quickly! 🧠' },
            {
                type: 'crossword',
                metaPhrase: 'LAKE DISTRICT',
                message: 'Where our next steps are. 🌳🌳🌳',
                clues: [
                    {
                        id: 'A1',
                        number: 1,
                        clue: 'Always on tracks',
                        answer: 'TRAINS',
                        startRow: 0,
                        startCol: 0,
                        direction: 'across'
                    },
                    {
                        id: 'D1',
                        number: 1,
                        clue: 'The best country on planet earth',
                        answer: 'THAILAND',
                        startRow: 0,
                        startCol: 0,
                        direction: 'down'
                    },
                    {
                        id: 'A2',
                        number: 2,
                        clue: 'Brain housing',
                        answer: 'HEAD',
                        startRow: 0,
                        startCol: 7,
                        direction: 'across'
                    },
                    {
                        id: 'D2',
                        number: 2,
                        clue: 'Inner voice I\'m learning to follow',
                        answer: 'HEART',
                        startRow: 0,
                        startCol: 7,
                        direction: 'down'
                    },
                    {
                        id: 'D3',
                        number: 3,
                        clue: 'Art you\'ve mastered',
                        answer: 'COOKING',
                        startRow: 2,
                        startCol: 2,
                        direction: 'down'
                    },
                    {
                        id: 'A4',
                        number: 4,
                        clue: 'Your ___ to my Rakan',
                        answer: 'XAYAH',
                        startRow: 2,
                        startCol: 4,
                        direction: 'across'
                    },
                    {
                        id: 'A5',
                        number: 5,
                        clue: 'Did you know this isn\'t plural?',
                        answer: 'DUCK',
                        startRow: 2,
                        startCol: 10,
                        direction: 'across'
                    },
                    {
                        id: 'D6',
                        number: 6,
                        clue: '____JWMWTUMCITCW', // FLPS Just want my wife to understand my clues in this cross word
                        answer: 'FLPS',
                        startRow: 3,
                        startCol: 14,
                        direction: 'down'
                    },
                    {
                        id: 'A7',
                        number: 7,
                        clue: 'Place polite people poo',
                        answer: 'LOO',
                        startRow: 4,
                        startCol: 0,
                        direction: 'across'
                    },
                    {
                        id: 'D8',
                        number: 8,
                        clue: 'Common twin excuse for losing',
                        answer: 'ORLAG',
                        startRow: 4,
                        startCol: 1,
                        direction: 'down'
                    },
                    {
                        id: 'A9',
                        number: 9,
                        clue: 'Type of office where I made you mine',
                        answer: 'DISTRICT',
                        startRow: 4,
                        startCol: 4,
                        direction: 'across'
                    },
                    {
                        id: 'D10',
                        number: 10,
                        clue: 'Picked out after every meal, so it feels complete',
                        answer: 'CHOCOLATE',
                        startRow: 4,
                        startCol: 10,
                        direction: 'down'
                    },
                    {
                        id: 'A11',
                        number: 11,
                        clue: 'Animal taxi, biblically"',
                        answer: 'ARK',
                        startRow: 5,
                        startCol: 0,
                        direction: 'across'
                    },
                    {
                        id: 'D12',
                        number: 12,
                        clue: 'Your fav',
                        answer: 'UDON',
                        startRow: 5,
                        startCol: 12,
                        direction: 'down'
                    },
                    {
                        id: 'A13',
                        number: 13,
                        clue: 'Dublin literary institution, briefly',
                        answer: 'NLI',
                        startRow: 6,
                        startCol: 0,
                        direction: 'across'
                    },
                    {
                        id: 'D14',
                        number: 14,
                        clue: 'Our favourite fruit',
                        answer: 'MELON',
                        startRow: 6,
                        startCol: 4,
                        direction: 'down'
                    },
                    {
                        id: 'D15',
                        number: 15,
                        clue: 'What you are literally right now',
                        answer: 'CLEVER',
                        startRow: 6,
                        startCol: 7,
                        direction: 'down'
                    },
                    {
                        id: 'A16',
                        number: 16,
                        clue: 'Questionable eatery we loved',
                        answer: 'GOODYS',
                        startRow: 6,
                        startCol: 9,
                        direction: 'across'
                    },
                    {
                        id: 'A17',
                        number: 17,
                        clue: 'Kitchen moves',
                        answer: 'DANCE',
                        startRow: 7,
                        startCol: 0,
                        direction: 'across'
                    },
                    {
                        id: 'A18',
                        number: 18,
                        clue: 'Esports organization operated by an NBA team',
                        answer: 'GG',
                        startRow: 8,
                        startCol: 1,
                        direction: 'across'
                    },
                    {
                        id: 'A19',
                        number: 19,
                        clue: 'Body of water surrounded by land',
                        answer: 'LAKE',
                        startRow: 8,
                        startCol: 4,
                        direction: 'across'
                    },
                    {
                        id: 'A20',
                        number: 20,
                        clue: 'Bearing between N and NE',
                        answer: 'NBE',
                        startRow: 8,
                        startCol: 12,
                        direction: 'across'
                    },
                    {
                        id: 'D21',
                        number: 21,
                        clue: 'With 32-Across, game you dominate',
                        answer: 'BEAT',
                        startRow: 8,
                        startCol: 13,
                        direction: 'down'
                    },
                    {
                        id: 'A22',
                        number: 22,
                        clue: 'Draft choice',
                        answer: 'ALE',
                        startRow: 9,
                        startCol: 9,
                        direction: 'across'
                    },
                    {
                        id: 'A23',
                        number: 23,
                        clue: 'The best country on planet earth',
                        answer: 'JAPAN',
                        startRow: 10,
                        startCol: 0,
                        direction: 'across'
                    },
                    {
                        id: 'D24',
                        number: 24,
                        clue: 'Father, for short',
                        answer: 'PAPA',
                        startRow: 10,
                        startCol: 2,
                        direction: 'down'
                    },
                    {
                        id: 'A25',
                        number: 25,
                        clue: 'What takes time to rebuild',
                        answer: 'TRUST',
                        startRow: 11,
                        startCol: 6,
                        direction: 'across'
                    },
                    {
                        id: 'D26',
                        number: 26,
                        clue: 'Home',
                        answer: 'UDB',
                        startRow: 11,
                        startCol: 8,
                        direction: 'down'
                    },
                    {
                        id: 'D27',
                        number: 27,
                        clue: 'My Fav',
                        answer: 'TEA',
                        startRow: 12,
                        startCol: 4,
                        direction: 'down'
                    },
                    {
                        id: 'A28',
                        number: 28,
                        clue: 'You',
                        answer: 'EGG',
                        startRow: 12,
                        startCol: 10,
                        direction: 'across'
                    },
                    {
                        id: 'D29',
                        number: 29,
                        clue: 'Delicious pods',
                        answer: 'GU',
                        startRow: 12,
                        startCol: 11,
                        direction: 'down'
                    },
                    {
                        id: 'D30',
                        number: 30,
                        clue: 'Nation\'s output',
                        answer: 'GDP',
                        startRow: 12,
                        startCol: 12,
                        direction: 'down'
                    },
                    {
                        id: 'D31',
                        number: 31,
                        clue: 'Block that goes boom in Minecraft',
                        answer: 'TNT',
                        startRow: 12,
                        startCol: 14,
                        direction: 'down'
                    },
                    {
                        id: 'A32',
                        number: 32,
                        clue: 'With 21-Down, a good workout',
                        answer: 'SABER',
                        startRow: 13,
                        startCol: 1,
                        direction: 'across'
                    },
                    {
                        id: 'A33',
                        number: 33,
                        clue: 'Six-pack units',
                        answer: 'ABS',
                        startRow: 13,
                        startCol: 7,
                        direction: 'across'
                    },
                    {
                        id: 'A34',
                        number: 34,
                        clue: 'Kaedama, with 12-Across',
                        answer: 'UDON',
                        startRow: 13,
                        startCol: 11,
                        direction: 'across'
                    },
                ],
            },
            { type: 'timegate', message: 'Nicely Done! The next step will unlock tomorrow. 🌳🌳🌳', unlockAfterHours: 24, completionMessage: 'Sorry about the wait. Without this you\'d solve them all too quickly! 🧠' },
            {
                type: 'connections',
                groups: [
                    { title: 'Things from the sea, beginning with S', items: ['SEAWEED', 'SALMON', 'SALT', 'SHELLS'], difficulty: 'yellow' },
                    { title: 'Ring Components', items: ['GEMSTONE', 'BAND', 'CUT', 'SETTING'], difficulty: 'green' },
                    { title: '5 Letter words you\'d find in our kitchen', items: ['LINDT', 'BACON', 'SPICE', 'DANCE'], difficulty: 'blue' },
                    { title: 'What we call each other', items: ['KIT KAT', 'BOO', 'TOMATO', 'EGG'], difficulty: 'purple' },
                ],
                message: 'Something to look forward to. 💍',
            }
        ],
        []
    );

    function markSolved(index: number) {
        setProgress((prev) => {
            const next = [...prev.solved];
            next[index] = true;
            return { solved: next };
        });
    }

    const [activeIndex, setActiveIndex] = useLocalStorage<number>('katepw:activeIndex:v1', 0);

    const maxUnlockedIndex = useMemo(() => {
        let lastUnlocked = 0;
        for (let i = 0; i < steps.length; i += 1) {
            if (i === 0 || progress.solved[i - 1]) {
                lastUnlocked = i;
            } else {
                break;
            }
        }
        return lastUnlocked;
    }, [steps, progress.solved]);

    useEffect(() => {
        if (activeIndex > maxUnlockedIndex) {
            setActiveIndex(maxUnlockedIndex);
        }
    }, [activeIndex, maxUnlockedIndex, setActiveIndex]);

    const allDone = progress.solved.every(Boolean);
    const isNextUnlocked = activeIndex < maxUnlockedIndex;
    const prevUnlockedRef = useRef<boolean>(isNextUnlocked);
    const [justUnlocked, setJustUnlocked] = useState(false);
    useEffect(() => {
        const wasUnlocked = prevUnlockedRef.current;
        if (!wasUnlocked && isNextUnlocked) {
            setJustUnlocked(true);
            const t = setTimeout(() => setJustUnlocked(false), 1200);
            return () => clearTimeout(t);
        }
        prevUnlockedRef.current = isNextUnlocked;
    }, [isNextUnlocked]);

    function goPrev() {
        if (activeIndex > 0) setActiveIndex(activeIndex - 1);
    }

    function goNext() {
        if (activeIndex < maxUnlockedIndex) setActiveIndex(activeIndex + 1);
    }

    return (
        <div className="App container">

            <div style={{ marginBottom: 28 }}>
                <Step
                    title={`Step ${activeIndex + 1}`}
                    isUnlocked={activeIndex === 0 || progress.solved[activeIndex - 1]}
                    isSolved={progress.solved[activeIndex]}
                    storageKey={`katepw:step:${activeIndex}:v1`}
                    solvedMessage={steps[activeIndex].type === 'timegate' ? undefined : steps[activeIndex].message}
                    onSolved={() => markSolved(activeIndex)}
                >
                    {(injected) => (
                        <Puzzle
                            step={steps[activeIndex]}
                            storageKey={injected.storageKey}
                            initiallySolved={injected.initiallySolved}
                            disabled={injected.disabled}
                            onSolved={injected.onSolved}
                        />
                    )}
                </Step>

            </div>

            {allDone && (
                <div style={{ marginTop: 16, fontSize: 18 }}>All steps complete. ❤️</div>
            )}

            <div className={`next-cta ${justUnlocked ? 'animate' : ''}`}>
                <button className="btn-ghost" onClick={goPrev} disabled={activeIndex === 0} aria-label="Previous step">◀ Back</button>
                {isNextUnlocked && (
                    <button className="btn-primary" onClick={goNext} aria-label="Next step">Next step ▶</button>
                )}
            </div>
        </div>
    );
}

export default App;
